/**
 * Pure URL builders for workspace entity pages (Files folders, Products,
 * Brands and Avatars). The app and Convex navigation share this module so a
 * server-built destination is byte-identical to the link the UI renders.
 *
 * Presentation only: every destination re-resolves identity and current VFS
 * access when it opens. Keep this module free of Convex runtime imports.
 */

export const WORKSPACE_FILES_NODE_REFERENCE_PARAM = 'node-reference';

/** Structural form of `WorkspaceNodeReference`; ids are opaque strings here. */
export type WorkspaceRouteNodeReference = {
  version: 1;
  kind: 'file' | 'directory';
  vfsNodeId: string;
  pathHint?: string;
};

export function workspaceFilesHref(orgSlug: string, path: string): string {
  const normalizedPath = path.replace(/^\/+/, '');
  const orgPrefix = `${orgSlug}/`;
  const routePath =
    normalizedPath === orgSlug
      ? ''
      : normalizedPath.startsWith(orgPrefix)
        ? normalizedPath.slice(orgPrefix.length)
        : normalizedPath;
  const encodedPath = routePath
    .split('/')
    .filter(Boolean)
    .map((segment) =>
      segment.startsWith('@')
        ? `@${encodeURIComponent(segment.slice(1))}`
        : encodeURIComponent(segment)
    )
    .join('/');
  return `/${orgSlug}/files/${encodedPath}`;
}

export function workspaceFilesNodeReferenceHref(
  orgSlug: string,
  path: string,
  reference: WorkspaceRouteNodeReference
): string {
  const search = new URLSearchParams({
    [WORKSPACE_FILES_NODE_REFERENCE_PARAM]: JSON.stringify(reference),
  });
  return `${workspaceFilesHref(orgSlug, path)}?${search.toString()}`;
}

/** A native brand opens as its Files folder, pinned to the node when known. */
export function brandFolderHref(
  orgSlug: string,
  path: string,
  reference?: WorkspaceRouteNodeReference | null
): string {
  return reference?.kind === 'directory'
    ? workspaceFilesNodeReferenceHref(orgSlug, path, reference)
    : workspaceFilesHref(orgSlug, path);
}

export const FILE_NATIVE_PRODUCT_SECTIONS: readonly [
  'media-studio',
  'catalog-data',
  'prints-and-patterns',
  '3d-models',
] = ['media-studio', 'catalog-data', 'prints-and-patterns', '3d-models'];

export type FileNativeProductSection =
  (typeof FILE_NATIVE_PRODUCT_SECTIONS)[number];

// Keep this list aligned with product subfolders that can own distinct
// artifact UIs. Adding a section here only defines routing; add the actual UI
// renderer in folder-artifact-renderer-registry or FileNativeProductRoute.
export const DEFAULT_FILE_NATIVE_PRODUCT_SECTION: FileNativeProductSection =
  'media-studio';

export function normalizeProductFolderPath(path: string): string {
  const normalized = path.trim().replace(/\/+/g, '/').replace(/\/+$/g, '');
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

export function fileNativeProductRouteHref(args: {
  orgSlug: string;
  idOrSlug: string;
  section?: FileNativeProductSection;
  productFolderPath?: string;
  search?: string;
}): string {
  const encodedId = encodeURIComponent(args.idOrSlug);
  const section = args.section ? `/${args.section}` : '';
  const searchParams = new URLSearchParams(
    args.search?.startsWith('?') ? args.search.slice(1) : args.search
  );
  if (args.productFolderPath) {
    searchParams.set(
      'productPath',
      normalizeProductFolderPath(args.productFolderPath)
    );
  }
  const serializedSearch = searchParams.toString();
  const search = serializedSearch ? `?${serializedSearch}` : '';
  return `/${args.orgSlug}/products/${encodedId}${section}${search}`;
}

/** The product card destination: the folder's default section. */
export function fileNativeProductFolderHref(
  orgSlug: string,
  path: string
): string {
  const productSegment = path.split('/').filter(Boolean).at(-1) ?? path;
  return fileNativeProductRouteHref({
    orgSlug,
    idOrSlug: productSegment,
    section: DEFAULT_FILE_NATIVE_PRODUCT_SECTION,
    productFolderPath: path,
  });
}

const AVATAR_NAVIGATION_ORIGIN = 'https://avatars.imai.local';

export function workspaceAvatarReturnPath(args: {
  orgSlug: string;
  returnTo?: string | null;
}): string | null {
  const { orgSlug, returnTo } = args;
  if (
    !returnTo?.startsWith('/') ||
    returnTo.startsWith('//') ||
    returnTo.includes('\\')
  ) {
    return null;
  }

  const parsed = new URL(returnTo, AVATAR_NAVIGATION_ORIGIN);
  const designPath = `/${orgSlug}/design`;
  const exactEntityPathPrefixes = [
    `/${orgSlug}/threads/`,
    `/${orgSlug}/products/`,
    `/${orgSlug}/projects/`,
  ];
  const entityId = exactEntityPathPrefixes
    .map((prefix) =>
      parsed.pathname.startsWith(prefix)
        ? parsed.pathname.slice(prefix.length)
        : ''
    )
    .find(Boolean);
  const isComposerPath =
    parsed.pathname === designPath ||
    (Boolean(entityId) &&
      !entityId?.includes('/') &&
      !/%2f/i.test(entityId ?? ''));

  if (parsed.origin !== AVATAR_NAVIGATION_ORIGIN || !isComposerPath) {
    return null;
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function avatarRouteSegment(path: string): string {
  return path.split('/').filter(Boolean).at(-1) ?? path;
}

export function workspaceFilesAvatarListHref(orgSlug: string): string {
  return `/${orgSlug}/files/avatars`;
}

export function workspaceFilesAvatarDetailHref(args: {
  orgSlug: string;
  path: string;
  returnTo?: string | null;
}): string {
  const routeSegment = avatarRouteSegment(args.path);
  const searchParams = new URLSearchParams({ avatarPath: args.path });
  const returnTo = workspaceAvatarReturnPath(args);
  if (returnTo) searchParams.set('returnTo', returnTo);
  return `${workspaceFilesAvatarListHref(args.orgSlug)}/${encodeURIComponent(routeSegment)}?${searchParams.toString()}`;
}
